import * as React from "react"
import ListItemIcon from "@mui/material/ListItemIcon"
import { styled, useTheme } from "@mui/material/styles"
import ListItemText from "@mui/material/ListItemText"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import ListItemButton from "@mui/material/ListItemButton"
import { SidebarContext } from "./Sidebar"
import Links from "./Links"
import { Badge } from "@mui/material"

// Estilos pré-definidos para evitar reconstruções a cada renderização
const createListItemStyles = (customizer, borderRadius, disabled) =>
    styled(ListItemButton)(() => ({
        whiteSpace: "nowrap",
        marginBottom: "2px",
        padding: !customizer.isCollapse ? "10px 12px" : "10px",
        width: "100%",
        textAlign: customizer.isCollapse ? "center" : "left",
        borderRadius: borderRadius,
        color: customizer.textColor,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? "0.6" : "1",
        display: customizer.isCollapse && "flex",
        justifyContent: customizer.isCollapse && "center",
        ".MuiListItemIcon-root": {
            color: customizer.textColor,
        },
        // As regras de hover e selected foram mantidas como comentadas
        "&.Mui-selected": {
            // color: "white",
            // backgroundColor: customizer.themeColor + 20,
            "&:hover": {
                // backgroundColor: customizer.themeColor + 20,
            },
        },
    }))

// Componente MenuItem otimizado com React.memo para evitar re-renderizações desnecessárias
const MenuItem = React.memo(
    ({
        children,
        icon,
        component,
        badge = false,
        link = "/",
        badgeColor = "secondary",
        badgeContent = "6",
        textFontSize = "14px",
        borderRadius = "8px",
        disabled = false,
        target = "",
        isSelected = false,
    }) => {
        const customizer = React.useContext(SidebarContext)
        const theme = useTheme()

        // Memoizar os estilos para evitar recriação
        const ListItemStyled = React.useMemo(
            () => createListItemStyles(customizer, borderRadius, disabled),
            [customizer, borderRadius, disabled],
        )

        // Memoizar o estilo do ícone
        const ListIConStyled = React.useMemo(
            () =>
                styled(ListItemIcon)(() => ({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "0px",
                    padding: "0px",
                    cursor: "pointer",
                    color: theme.palette.textPrimary,
                })),
            [theme.palette.textPrimary],
        )

        // Memoizar o ícone para evitar re-renderizações
        const iconElement = React.useMemo(() => icon || "", [icon])

        // Memoizar o conteúdo do badge
        const badgeElement = React.useMemo(() => {
            if (badge) {
                return <Badge badgeContent={badgeContent} color={badgeColor} />
            }
            return null
        }, [badge, badgeContent, badgeColor])

        // Memoizar o texto do item
        const textElement = React.useMemo(() => {
            if (!customizer.isCollapse) {
                return (
                    <>
                        <ListItemText sx={{ my: 0 }}>
                            <Typography
                                fontSize={textFontSize}
                                sx={{ lineHeight: "1" }}
                                variant="caption"
                            >
                                {children}
                            </Typography>
                        </ListItemText>
                        {badgeElement}
                    </>
                )
            }
            return null
        }, [customizer.isCollapse, textFontSize, children, badgeElement])

        // Impedir remontagem das props do ListItemStyled
        const listItemProps = React.useMemo(
            () => ({
                sx: { display: "flex", gap: "15px" },
                target: target,
                selected: isSelected,
            }),
            [target, isSelected],
        )

        // Impedir remontagem das props do ListIConStyled
        const listIconProps = React.useMemo(
            () => ({
                sx: { minWidth: "0px" },
            }),
            [],
        )

        return (
            <Box>
                <Links component={component} href={link} to={link}>
                    <ListItemStyled {...listItemProps}>
                        <ListIConStyled {...listIconProps}>
                            {iconElement}
                        </ListIConStyled>
                        {textElement}
                    </ListItemStyled>
                </Links>
            </Box>
        )
    },
    // Função de comparação personalizada para o React.memo
    (prevProps, nextProps) => {
        // Só re-renderizar se estas props mudarem
        return (
            prevProps.icon === nextProps.icon &&
            prevProps.isSelected === nextProps.isSelected &&
            prevProps.children === nextProps.children &&
            prevProps.link === nextProps.link &&
            prevProps.badge === nextProps.badge &&
            prevProps.badgeContent === nextProps.badgeContent
        )
    },
)

// Definir displayName explicitamente para debugging
MenuItem.displayName = "MenuItem"

export { MenuItem }
